import {
    ADDITIONAL_EMAILS_RESOURCE_NAME,
    MAX_CHATS_RESOURCE_NAME,
    TELEGRAM_CHATS_RESOURCE_NAME,
} from 'shared/config/model/urls';

import {DefaultValues} from 'react-hook-form';

import CreateDomainMonitorType from 'features/domain/model/DomainMonitor/CreateDomainMonitorType';
import IDomainMonitorOutput from 'features/domain/model/DomainMonitor/IDomainMonitor';
import {IUserOutput} from 'entities/user';
import {
    getDomainChecksDefaults,
    buildCommonBatchRequest,
    IBatchOperationRequest,
    IBuildBatchRequestParams,
    normalizeTags,
} from 'entities/monitor';
import {idsToIri, normalizeIds} from 'shared/api/iriHelper';

import {buildDomainBatchEditPayload, DomainBatchEditFormValues} from './domainBatchEditFields';

export type DomainBatchOperationsFormValues = DomainBatchEditFormValues & {url: string};

export function getDomainMonitorFormDefaults(
    item: IDomainMonitorOutput | undefined,
    user: IUserOutput | null,
): DefaultValues<CreateDomainMonitorType> {
    const isNew = !item;
    const checksDefaults = getDomainChecksDefaults(user);
    const isChecked = (value: boolean | undefined, defaultForNew: boolean) => (isNew ? defaultForNew : !!value);

    return {
        domain: item?.domain_human_readable,
        active: isNew ? true : item.active,
        ssl_watch: isChecked(item?.ssl_watch, checksDefaults.ssl_watch),
        domain_watch: isChecked(item?.domain_watch, checksDefaults.domain_watch),
        ssl_redirect_watch: isChecked(item?.ssl_redirect_watch, checksDefaults.ssl_redirect_watch),
        robots_txt_watch: isChecked(item?.robots_txt_watch, checksDefaults.robots_txt_watch),
        rkn_watch: isChecked(item?.rkn_watch, checksDefaults.rkn_watch),
        notify_by_email: isChecked(item?.notify_by_email, !!user?.email_confirmed),
        telegram_chats: normalizeIds(item?.telegram_chats),
        max_chats: normalizeIds(item?.max_chats),
        additional_emails: normalizeIds(item?.additional_emails),
        tags: item?.tags?.map(tag => tag.label) ?? [],
    };
}

export function getDomainBatchCreateDefaults(user: IUserOutput | null): DefaultValues<CreateDomainMonitorType> {
    return {
        domain: '',
        active: true,
        ...getDomainChecksDefaults(user),
        notify_by_email: !!user?.email_confirmed,
        telegram_chats: [],
        max_chats: [],
        additional_emails: [],
        tags: [],
    };
}

export function getDomainBatchOperationsDefaults(
    user: IUserOutput | null,
): DefaultValues<DomainBatchOperationsFormValues> {
    return {
        url: '',
        ssl_watch: false,
        domain_watch: false,
        ssl_redirect_watch: false,
        robots_txt_watch: false,
        rkn_watch: false,
        notify_by_email: !!user?.email_confirmed,
        telegram_chats: [],
        max_chats: [],
        additional_emails: [],
        tags: [],
    };
}

export function buildDomainMonitorSettings<T extends Omit<CreateDomainMonitorType, 'domain'>>(
    {tags, ...rest}: T,
    isEnglish: boolean,
): T {
    const data = {
        ...rest,
        telegram_chats: idsToIri(rest.telegram_chats, TELEGRAM_CHATS_RESOURCE_NAME),
        max_chats: idsToIri(rest.max_chats, MAX_CHATS_RESOURCE_NAME),
        additional_emails: idsToIri(rest.additional_emails, ADDITIONAL_EMAILS_RESOURCE_NAME),
        tags: tags ? normalizeTags(tags as string[]) : [],
    } as unknown as T;

    if (isEnglish) {
        data.rkn_watch = false;
    }

    return data;
}

export function buildDomainBatchOperationRequest({
    actionValue,
    formData,
    addedFields,
    selectedIds,
}: IBuildBatchRequestParams<DomainBatchOperationsFormValues>): IBatchOperationRequest {
    switch (actionValue) {
        case 'edit':
            return {method: 'PUT', data: buildDomainBatchEditPayload(addedFields, formData, selectedIds)};
        case 'remove-by-url':
            return {method: 'DELETE', data: {url: formData.url}};
        case 'export':
            return {method: 'POST', data: {ids: selectedIds}};
        default:
            return buildCommonBatchRequest(actionValue, selectedIds, formData.tags as string[]);
    }
}
